import { useCustomerQuery } from '@graphcommerce/magento-customer'
import { ApolloCustomerErrorAlert } from '@graphcommerce/magento-customer/components/ApolloCustomerError/ApolloCustomerErrorAlert'
import { useFormGqlMutation } from '@graphcommerce/react-hook-form'
import { i18n } from '@lingui/core'
import { Trans } from '@lingui/react'
import { Button, MenuItem } from '@mui/material'
import { GraphQLError } from 'graphql'
import { useState } from 'react'
import { StandardButton } from '../../Button/ButtonStyles'
import { StandardDropdown } from '../../Dropdown/StandardDropdown'
import { Message } from '../../Message'
import { AlertPrompt } from '../../Prompt/AlertPrompt'
import { StandardTextField } from '../../TextInput/TextFieldStyles'
import { SubRolesDocument } from '../SubRole/SubRoles.gql'
import { SubUserFragment } from '../SubUser/SubUser.gql'
import { CreateSubUserDocument } from './CreateSubUser.gql'
import { EditSubUserDocument } from './EditSubUser.gql'

type SubUserFormProps = {
  onComplete?: (args?: any) => void
  onCancel?: () => void
  onError?: (errors: readonly GraphQLError[] | undefined) => void
  onSuccess?: (mode: 'NEW' | 'EDIT', subEmail?: any) => void
  user?: SubUserFragment
}

export function SubUserForm(props: SubUserFormProps) {
  const { onComplete, onError, onSuccess, onCancel, user } = props
  const [message, setMessage] = useState<{ type: 'error' | 'success'; message: string } | null>(
    null,
  )
  const { data, loading: rolesLoading } = useCustomerQuery(SubRolesDocument, {
    fetchPolicy: 'cache-and-network',
  })
  const roles = data?.customer?.sub_roles

  const Mutation = user ? EditSubUserDocument : CreateSubUserDocument

  const form = useFormGqlMutation(
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    Mutation,
    {
      defaultValues: {
        subId: user?.sub_id,
        subStatus: 1, // 1 = Enabled
        subEmail: user?.sub_email || '',
        subName: user?.sub_name,
        roleId: user?.role_id || 0, // 0 = admin
      },
      onComplete: ({ data: response, errors }) => {
        if (
          response?.[user ? 'bssCompanyAccountEditSubUser' : 'bssCompanyAccountCreateSubUser'] ===
          false
        ) {
          setMessage({ type: 'error', message: i18n._(/* i18n */ 'User already exists.') })
        } else {
          if (errors?.length) {
            if (onError) onError(errors)
          } else if (onSuccess)
            onSuccess(user ? 'EDIT' : 'NEW', user?.sub_email ?? form?.valid?.subEmail)
          if (onComplete) onComplete()
        }
      },
    },
    { errorPolicy: 'all' },
  )
  const [displayPrompt, setDisplayPrompt] = useState<boolean>(false)

  const handleClosePrompt = () => {
    setDisplayPrompt(false)
  }

  const { handleSubmit, formState, required, error, register, watch } = form

  const submitHandler = handleSubmit((_, e) => {
    setDisplayPrompt(true)
    if (!formState.errors) {
      e?.target.reset()
    }
  })

  if (typeof data === 'undefined' && rolesLoading) {
    return null
  }

  const options =
    roles?.map((role) => ({ value: role?.role_id || 0, label: role?.role_name || '' })) || []
  const roleMenuOptions = options.map((option) => (
    <MenuItem value={option.value} className='flex w-full items-center justify-between'>
      <span>{option.label}</span>
    </MenuItem>
  ))
  return (
    <>
      <form
        onSubmit={submitHandler}
        noValidate
        className='customer-address-form flex flex-col items-center justify-center gap-x-5 gap-y-5 md:grid md:grid-cols-2 md:gap-y-6'
      >
        <StandardTextField
          type='text'
          label={<Trans id='User Name' />}
          variant='outlined'
          color='primary'
          required
          className='w-[260px] max-w-[260px]'
          size='small'
          error={!!formState.errors.subName}
          {...register('subName', {
            required: true,
          })}
        />
        <div className='hidden md:block' />
        <StandardTextField
          type='text'
          label={<Trans id='User Email' />}
          variant='outlined'
          error={!!formState.errors.subEmail}
          color='primary'
          required
          className='w-[260px] max-w-[260px]'
          size='small'
          {...register('subEmail', {
            required: true,
          })}
        />
        <StandardDropdown
          label='Role'
          required
          value={watch('roleId')}
          {...register('roleId', {
            validate: (value) => value !== null,
          })}
          onChange={(option) => {
            if (typeof option?.target?.value === 'number')
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              form.setValue('roleId', parseFloat(option?.target?.value.toString()))
          }}
          options={roleMenuOptions}
          sx={{
            width: '260px',
            maxWidth: '260px',
          }}
        />
        {/* <Dropdown
          selectProps={{ name: 'role' }}
          topLabels={['Role*']}
          topLabelsClassName='md:pt-[4px] md:pb-[3px]'
          className='Type-Large-Regular w-full'
          required
          value={watch('roleId')}
          {...register('roleId', {
            validate: (value) => value !== null,
          })}
          helperText={formState.errors.roleId?.message}
          optionLabelCountInMainArea={1}
          onChange={(option) => {
            if (typeof option.value === 'number')
              form.setValue('roleId', parseFloat(option.value.toString()))
          }}
          options={
            roles?.map((role) => ({ value: role?.role_id || 0, label: role?.role_name || '' })) ||
            []
          }
          alignSelected='left'
        /> */}

        {message && (
          <Message type={message.type} content={message.message} containerClassName='my-[15px]' />
        )}
        <div className='mt-5 flex flex-col items-center justify-start gap-6 gap-y-5 md:flex-row'>
          <StandardButton
            type='submit'
            className='w-full max-w-[350px] bg-material-ui-blue uppercase md:w-auto'
            variant='contained'
            loading={formState.isSubmitting}
            size='small'
          >
            {i18n._('Save and Send Invite')}
          </StandardButton>
          <Button
            color='primary'
            className='w-full max-w-[350px] bg-pure-white uppercase md:w-auto'
            variant='outlined'
            onClick={onCancel}
            size='small'
          >
            {i18n._('Cancel')}
          </Button>
        </div>
      </form>

      <AlertPrompt
        title='Email Sent'
        message={`Email has been sent to ${user?.sub_email}`}
        isDisplayed={displayPrompt}
        setIsDisplayed={setDisplayPrompt}
        callback={handleClosePrompt}
      />
      <ApolloCustomerErrorAlert error={error} />
    </>
  )
}
